import React, { useState } from 'react';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CardMedia,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Checkbox,
  FormControlLabel,
  SelectChangeEvent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VideocamIcon from '@mui/icons-material/Videocam';
import BusinessIcon from '@mui/icons-material/Business';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

// Interface for events - export it to be used in other files
export interface EventData {
  id?: string;
  eventName: string;
  eventDate: string | { seconds: number; nanoseconds: number };
  eventDescription?: string;
  location?: string;
  startTime?: string | { seconds: number; nanoseconds: number };
  endTime?: string | { seconds: number; nanoseconds: number };
  isVirtual?: boolean;
  meetingLink?: string;
  source?: string;
  enableSocialSharing?: boolean;
  trackEngagementAnalytics?: boolean;
  addUTMParameters?: boolean;
  name?: string;
  date?: string | { seconds: number; nanoseconds: number };
  title?: string;
  event_name?: string;
  description?: string;
  desc?: string;
  startDate?: string | { seconds: number; nanoseconds: number };
  start_date?: string | { seconds: number; nanoseconds: number };
  event_date?: string | { seconds: number; nanoseconds: number };
  venue?: string;
  place?: string;
}

interface EventSelectionStepProps {
  events: EventData[];
  selectedEvent: EventData | null;
  onSelectEvent: (event: EventData) => void;
  loading: boolean;
}

// Define a type for Firestore timestamp to use throughout the component
type FirestoreTimestamp = {
  seconds: number;
  nanoseconds: number;
};

const EventSelectionStep: React.FC<EventSelectionStepProps> = ({
  events,
  selectedEvent,
  onSelectEvent,
  loading,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [createEventOpen, setCreateEventOpen] = useState(false);
  const [newEvent, setNewEvent] = useState<EventData>({
    eventName: '',
    eventDate: '',
    eventDescription: '',
    location: '',
    startTime: '',
    endTime: '',
    isVirtual: false,
    meetingLink: '',
    source: 'system',
    enableSocialSharing: true,
    trackEngagementAnalytics: true,
    addUTMParameters: true,
  });
  const [eventFormErrors, setEventFormErrors] = useState<Record<string, string>>({});

  // Format date for display
  const formatDate = (dateInput: any): string => {
    if (!dateInput) return 'Date not set';
    
    try {
      let date;
      
      // Handle Firestore timestamp objects
      if (typeof dateInput === 'object' && dateInput !== null) {
        // Check if it's a Firestore timestamp (has seconds and nanoseconds)
        if (dateInput.seconds !== undefined && dateInput.nanoseconds !== undefined) {
          // Convert Firestore timestamp to JS Date
          date = new Date(dateInput.seconds * 1000);
        } else if (Object.prototype.toString.call(dateInput) === '[object Date]') {
          date = dateInput as Date;
        } else {
          // If it's some other object, try to stringify it
          return String(dateInput);
        }
      } else {
        // Handle string dates
        date = new Date(dateInput);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return String(dateInput);
      }
      
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      // Return a string representation as fallback
      return String(dateInput);
    }
  };

  // Format time for display
  const formatTime = (timeString: string | FirestoreTimestamp | undefined | null): string => {
    if (!timeString) return '';
    
    // If already in HH:MM format, just return it
    if (typeof timeString === 'string' && /^\d{1,2}:\d{2}(:\d{2})?(?: [AP]M)?$/i.test(timeString)) {
      return timeString;
    }
    
    try {
      let date;
      
      // Handle Firestore timestamp objects
      if (typeof timeString === 'object' && timeString !== null) {
        // Check if it's a Firestore timestamp (has seconds and nanoseconds)
        if ('seconds' in timeString && 'nanoseconds' in timeString) {
          date = new Date(timeString.seconds * 1000);
        } else if (Object.prototype.toString.call(timeString) === '[object Date]') {
          date = timeString as Date;
        } else {
          return String(timeString);
        }
      } else if (typeof timeString === 'string') {
        // Try to parse as a date if it's a full datetime string
        if (timeString.includes('T') || timeString.includes('-') || timeString.includes('/')) {
          date = new Date(timeString);
        } else {
          return timeString;
        }
      } else {
        return String(timeString);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return String(timeString);
      }
      
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch (e) {
      return String(timeString);
    }
  };

  // Get formatted datetime string (combines date and time if available)
  const getFormattedDateTime = (event: EventData): string => {
    const dateStr = formatDate(getEventDate(event));
    
    // Get time information
    let timeStr = '';
    if (event.startTime && event.endTime) {
      timeStr = `${formatTime(event.startTime)} - ${formatTime(event.endTime)}`;
    } else if (event.startTime) {
      timeStr = formatTime(event.startTime);
    } else if (event.eventDate) {
      // If the date string includes time information (ISO format)
      const dateInput = event.eventDate;
      
      if (typeof dateInput === 'object' && dateInput !== null && 
          'seconds' in dateInput && 'nanoseconds' in dateInput) {
        // It's a Firestore timestamp
        const date = new Date((dateInput as FirestoreTimestamp).seconds * 1000);
        timeStr = date.toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      } else if (typeof dateInput === 'string' && dateInput.includes('T')) {
        // It's an ISO string with time
        const date = new Date(dateInput);
        if (!isNaN(date.getTime())) {
          timeStr = date.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
        }
      }
    }
    
    return timeStr ? `${dateStr}, ${timeStr}` : dateStr;
  };

  // Helper function to get event name from various possible field names
  const getEventName = (event: EventData) => {
    return event.eventName || event.name || event.title || event.event_name || 'Unnamed Event';
  };

  // Helper function to get event date from various possible field names
  const getEventDate = (event: EventData) => {
    // Check each possible date field
    const dateField = event.eventDate || event.date || event.startDate || event.start_date || event.event_date;
    
    // Handle Firestore timestamp objects
    if (typeof dateField === 'object' && dateField !== null && 
        dateField.seconds !== undefined && dateField.nanoseconds !== undefined) {
      // Convert Firestore timestamp to ISO string for consistency
      return new Date(dateField.seconds * 1000).toISOString();
    }
    
    return dateField || '';
  };

  // Helper function to get event description from various possible field names
  const getEventDescription = (event: EventData) => {
    return event.eventDescription || event.description || event.desc || 'No description available';
  };

  // Helper function to get event location from various possible field names
  const getEventLocation = (event: EventData) => {
    return event.location || event.venue || event.place || '';
  };

  // Handle event search
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  // Filter events based on search query
  const filteredEvents = events.filter(event => {
    // Handle different variations of name fields
    const eventName = event.eventName || event.name || event.title || event.event_name || '';
    const eventDescription = event.eventDescription || event.description || event.desc || '';
    
    return eventName.toLowerCase().includes(searchQuery.toLowerCase()) ||
           eventDescription.toLowerCase().includes(searchQuery.toLowerCase());
  });

  // Handle new event form change
  const handleNewEventChange = (field: keyof EventData, value: string | boolean) => {
    setNewEvent(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field if it exists
    if (eventFormErrors[field]) {
      setEventFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Validate event form
  const validateEventForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!newEvent.eventName?.trim()) {
      errors.eventName = 'Event name is required';
    }
    
    if (!newEvent.eventDate) {
      errors.eventDate = 'Event date is required';
    }
    
    if (newEvent.isVirtual && !newEvent.meetingLink) {
      errors.meetingLink = 'Meeting link is required for virtual events';
    }
    
    setEventFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle create event
  const handleCreateEvent = () => {
    if (validateEventForm()) {
      // Add a temporary ID
      const createdEvent: EventData = {
        ...newEvent,
        id: `new-${Date.now()}`
      };
      
      // Close dialog and select the new event
      setCreateEventOpen(false);
      onSelectEvent(createdEvent);
      
      // Reset form
      setNewEvent({
        eventName: '',
        eventDate: '',
        eventDescription: '',
        location: '',
        startTime: '',
        endTime: '',
        isVirtual: false,
        meetingLink: '',
        source: 'system',
        enableSocialSharing: true,
        trackEngagementAnalytics: true,
        addUTMParameters: true,
      });
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select an Event
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Choose an event from your organization or create a new one to invite your contacts to.
      </Typography>

      <Box sx={{ display: 'flex', mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search events..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mr: 2 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateEventOpen(true)}
          color="primary"
        >
          Create Event
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredEvents.length > 0 ? (
        <Grid container spacing={2}>
          {filteredEvents.map((event) => (
            <Grid item xs={12} md={6} key={event.id}>
              <Card 
                variant={selectedEvent?.id === event.id ? 'elevation' : 'outlined'}
                sx={{ 
                  height: '100%',
                  borderColor: selectedEvent?.id === event.id ? 'primary.main' : undefined,
                  boxShadow: selectedEvent?.id === event.id ? 3 : undefined,
                }}
              >
                <CardActionArea 
                  onClick={() => onSelectEvent(event)}
                  sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}
                >
                  <CardContent sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="h6" component="h2" gutterBottom>
                        {getEventName(event)}
                      </Typography>
                      {event.isVirtual && (
                        <Chip 
                          icon={<VideocamIcon />} 
                          label="Virtual" 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                        />
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EventIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '0.9rem' }} />
                      <Typography variant="body2" color="text.secondary">
                        {getFormattedDateTime(event)}
                      </Typography>
                    </Box>
                    
                    {getEventLocation(event) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        {event.isVirtual ? (
                          <VideocamIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '0.9rem' }} />
                        ) : (
                          <LocationOnIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '0.9rem' }} />
                        )}
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {getEventLocation(event)}
                        </Typography>
                      </Box>
                    )}
                    
                    <Divider sx={{ my: 1.5 }} />
                    
                    <Typography variant="body2" color="text.secondary" sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}>
                      {getEventDescription(event)}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No events found
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            {searchQuery ? 
              "No events match your search. Try different keywords or create a new event." : 
              "You don't have any events in your organization yet. Create your first event to invite contacts."}
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateEventOpen(true)}
            size="large"
            sx={{ mt: 2 }}
          >
            Create New Event
          </Button>
        </Box>
      )}

      {/* Create Event Dialog */}
      <Dialog 
        open={createEventOpen} 
        onClose={() => setCreateEventOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create New Event</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Event Name"
                fullWidth
                required
                value={newEvent.eventName}
                onChange={(e) => handleNewEventChange('eventName', e.target.value)}
                error={!!eventFormErrors.eventName}
                helperText={eventFormErrors.eventName}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Event Date"
                type="date"
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
                value={newEvent.eventDate}
                onChange={(e) => handleNewEventChange('eventDate', e.target.value)}
                error={!!eventFormErrors.eventDate}
                helperText={eventFormErrors.eventDate}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={newEvent.isVirtual}
                    onChange={(e) => handleNewEventChange('isVirtual', e.target.checked)}
                  />
                }
                label="Virtual Event"
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="Start Time"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newEvent.startTime}
                onChange={(e) => handleNewEventChange('startTime', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                label="End Time"
                type="time"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newEvent.endTime}
                onChange={(e) => handleNewEventChange('endTime', e.target.value)}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Location"
                fullWidth
                value={newEvent.location}
                onChange={(e) => handleNewEventChange('location', e.target.value)}
                placeholder={newEvent.isVirtual ? 'Virtual' : 'Physical location'}
              />
            </Grid>
            
            {newEvent.isVirtual && (
              <Grid item xs={12}>
                <TextField
                  label="Meeting Link"
                  fullWidth
                  value={newEvent.meetingLink}
                  onChange={(e) => handleNewEventChange('meetingLink', e.target.value)}
                  error={!!eventFormErrors.meetingLink}
                  helperText={eventFormErrors.meetingLink}
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                label="Event Description"
                fullWidth
                multiline
                rows={4}
                value={newEvent.eventDescription}
                onChange={(e) => handleNewEventChange('eventDescription', e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Tracking & Analytics
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={newEvent.enableSocialSharing}
                    onChange={(e) => handleNewEventChange('enableSocialSharing', e.target.checked)}
                  />
                }
                label="Enable social sharing for this event"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={newEvent.trackEngagementAnalytics}
                    onChange={(e) => handleNewEventChange('trackEngagementAnalytics', e.target.checked)}
                  />
                }
                label="Track engagement analytics"
              />
              <FormControlLabel
                control={
                  <Checkbox 
                    checked={newEvent.addUTMParameters}
                    onChange={(e) => handleNewEventChange('addUTMParameters', e.target.checked)}
                  />
                }
                label="Add UTM parameters to shared links"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateEventOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleCreateEvent}
          >
            Create Event
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventSelectionStep;
